import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getPlaceTypeSuggestions, formatPlaceTypeDisplay } from '../../utils/supportedPlaceTypes';

const PlaceTypeDropdown = ({ 
  placeholder, 
  onAdd, 
  className = "",
  existingTypes = []
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (inputValue.length > 0) {
      const filteredSuggestions = getPlaceTypeSuggestions(inputValue)
        .filter(type => !existingTypes.includes(type));
      setSuggestions(filteredSuggestions);
      setShowDropdown(filteredSuggestions.length > 0);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  }, [inputValue, existingTypes]);

  // Calculate dropdown position to open to the right
  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top,
        left: rect.right + 8, // 8px gap from input
        width: 280 // Fixed width for dropdown
      });
    }
  }, [showDropdown]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = (e) => {
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
  };

  const handleSuggestionClick = (suggestion) => {
    addType(suggestion);
  };

  const addType = (type) => {
    if (type && !existingTypes.includes(type)) {
      onAdd(type);
      setInputValue('');
      setShowDropdown(false);
      setSelectedIndex(-1);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) {
      if (e.key === 'Enter' && inputValue.trim()) {
        const exactMatch = suggestions.find(s => 
          s.toLowerCase() === inputValue.toLowerCase() ||
          formatPlaceTypeDisplay(s).toLowerCase() === inputValue.toLowerCase()
        );
        if (exactMatch) {
          addType(exactMatch);
        }
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          addType(suggestions[selectedIndex]);
        } else if (suggestions.length === 1) {
          addType(suggestions[0]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full pl-7 pr-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 bg-white ${className}`}
          autoComplete="off"
        />
        <svg 
          className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      
      {showDropdown && suggestions.length > 0 && ReactDOM.createPortal(
        <div 
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
          className="bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-auto z-[10000]"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSuggestionClick(suggestion);
              }}
              className={`px-2.5 py-1.5 cursor-pointer text-sm flex items-center justify-between ${
                index === selectedIndex 
                  ? 'bg-blue-50 text-blue-900' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <span className="font-medium">{formatPlaceTypeDisplay(suggestion)}</span>
              <span className="text-[9px] text-gray-400 ml-2">{suggestion}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default PlaceTypeDropdown;